// src/store/slices/dnlToolsSlice.ts
/**
 * DNL (Double Number Line) Tools Redux Slice
 * Phase 1: Custom/Sparse Points System
 * 
 * Manages DNL-specific state including:
 * - Sparse tick mode
 * - Custom points for primary and secondary lines
 * - Line labels
 * - Unit suffixes
 * 
 * Based on DNL Enhancement Specification v1.0
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  DNLToolsState,
  DNLCustomTick,
  SparseTickMode,
  LineLabel,
  UnitSuffix,
  MappingArrowsConfig,
  MappingArrow,
  DNLAlternativeViews,
  ViewConfiguration,
  DNLContextPreset,
  TablePosition,
  RatioTableCell,
} from '../../types/dnl';
import {
  defaultDNLToolsState,
  defaultSparseTickMode,
  defaultLineLabel,
  defaultUnitSuffix,
  defaultMappingArrowsConfig,
  defaultAlternativeViews,
  defaultContextPresetState,
  defaultRatioTableConfig,
  captureCurrentView,
  applyViewConfiguration,
} from '../../types/dnl';
import { CONTEXT_PRESETS } from '../../lib/presets';
import type { Fraction } from '../../types/redux';

// =============================================================================
// STORAGE KEY
// =============================================================================

const DNL_TOOLS_STORAGE_KEY = 'nl:dnlTools';

// =============================================================================
// STORAGE HELPERS
// =============================================================================

/**
 * Load DNL tools state from localStorage
 */
function loadFromStorage(): DNLToolsState {
  try {
    const stored = localStorage.getItem(DNL_TOOLS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new properties
      return {
        sparseMode: { ...defaultSparseTickMode, ...parsed.sparseMode },
        primaryLabel: { ...defaultLineLabel, ...parsed.primaryLabel },
        secondaryLabel: { ...defaultLineLabel, ...parsed.secondaryLabel },
        primaryUnit: { ...defaultUnitSuffix, ...parsed.primaryUnit },
        secondaryUnit: { ...defaultUnitSuffix, ...parsed.secondaryUnit },
        mappingArrows: { ...defaultMappingArrowsConfig, ...parsed.mappingArrows },
        alternativeViews: { ...defaultAlternativeViews, ...parsed.alternativeViews },
        contextPreset: { ...defaultContextPresetState, ...parsed.contextPreset },
        ratioTable: { ...defaultRatioTableConfig, ...parsed.ratioTable },
      };
    }
  } catch (error) {
    console.warn('Failed to load DNL tools from storage:', error);
  }
  return { ...defaultDNLToolsState };
}

/**
 * Save DNL tools state to localStorage
 */
function saveToStorage(state: DNLToolsState): void {
  try {
    localStorage.setItem(DNL_TOOLS_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save DNL tools to storage:', error);
  }
}

// =============================================================================
// INITIAL STATE
// =============================================================================

// Re-export defaults for use in this file
const defaultSparseTickModeState: SparseTickMode = {
  enabled: false,
  hideRegularTicks: true,
  customTicks: [],
  showZero: true,
  nextId: 1,
};

const defaultLineLabelState: LineLabel = {
  text: '',
  subtext: undefined,
  position: 'left',
  visible: false,
};

const defaultUnitSuffixState: UnitSuffix = {
  text: '',
  position: 'after',
  enabled: false,
};

const defaultDNLToolsStateLocal: DNLToolsState = {
  sparseMode: defaultSparseTickModeState,
  primaryLabel: { ...defaultLineLabelState },
  secondaryLabel: { ...defaultLineLabelState },
  primaryUnit: { ...defaultUnitSuffixState },
  secondaryUnit: { ...defaultUnitSuffixState },
  mappingArrows: { ...defaultMappingArrowsConfig },
  alternativeViews: { ...defaultAlternativeViews },
  contextPreset: { ...defaultContextPresetState },
  ratioTable: { ...defaultRatioTableConfig },
};

const initialState: DNLToolsState = loadFromStorage();

// =============================================================================
// SLICE DEFINITION
// =============================================================================

export const dnlToolsSlice = createSlice({
  name: 'dnlTools',
  initialState,
  reducers: {
    // =========================================================================
    // SPARSE MODE ACTIONS
    // =========================================================================

    /**
     * Toggle sparse tick mode on/off
     */
    toggleSparseMode: (state) => {
      state.sparseMode.enabled = !state.sparseMode.enabled;
      saveToStorage(state);
    },

    /**
     * Set sparse mode enabled state
     */
    setSparseModeEnabled: (state, action: PayloadAction<boolean>) => {
      state.sparseMode.enabled = action.payload;
      saveToStorage(state);
    },

    /**
     * Toggle hiding of regular ticks in sparse mode
     */
    toggleHideRegularTicks: (state) => {
      state.sparseMode.hideRegularTicks = !state.sparseMode.hideRegularTicks;
      saveToStorage(state);
    },

    /**
     * Set hide regular ticks state
     */
    setHideRegularTicks: (state, action: PayloadAction<boolean>) => {
      state.sparseMode.hideRegularTicks = action.payload;
      saveToStorage(state);
    },

    /**
     * Toggle showing zero in sparse mode
     */
    toggleShowZero: (state) => {
      state.sparseMode.showZero = !state.sparseMode.showZero;
      saveToStorage(state);
    },

    // =========================================================================
    // CUSTOM TICK ACTIONS
    // =========================================================================

    /**
     * Add a new custom tick
     */
    addCustomTick: (state, action: PayloadAction<{
      value: number;
      line: 'primary' | 'secondary' | 'both';
      label?: string;
      style?: 'normal' | 'highlighted' | 'question';
      colour?: string;
    }>) => {
      const { value, line, label, style = 'normal', colour } = action.payload;

      // Check for duplicate on the same line
      const existingTick = state.sparseMode.customTicks.find(
        tick => Math.abs(tick.value - value) < 1e-10 && 
                (tick.line === line || tick.line === 'both' || line === 'both')
      );

      if (!existingTick) {
        // Only show label if there's a custom label or it's a question mark
        const shouldShowLabel = !!(label || style === 'question');
        const newTick: DNLCustomTick = {
          id: `dnl-tick-${state.sparseMode.nextId}`,
          value,
          label,
          line,
          showTick: true,
          showLabel: shouldShowLabel,
          style,
          colour,
          createdAt: Date.now(),
        };

        state.sparseMode.customTicks.push(newTick);
        state.sparseMode.nextId += 1;
        saveToStorage(state);
      }
    },

    /**
     * Add a custom tick with a corresponding point on the other line
     * Note: value is expected to be in domain (primary) coordinates
     */
    addCustomTickWithCorresponding: (state, action: PayloadAction<{
      value: number;
      line: 'primary' | 'secondary';
      label?: string;
      style?: 'normal' | 'highlighted' | 'question';
      colour?: string;
      scaleFactor: Fraction;
    }>) => {
      const { value, line, label, style = 'normal', colour, scaleFactor } = action.payload;
      
      // Value is already in domain coordinates (primary line coordinate system)
      // Both primary and secondary ticks at the same domain position are "corresponding"
      // So the corresponding value is the same domain value
      const correspondingValue = value;

      // For corresponding points, don't set a custom label - let the renderer calculate
      // the numeric value from tick.value. Only set label if user provided a custom label
      // or if it's a question mark style.
      const tickLabel = label || (style === 'question' ? '?' : undefined);

      // Add tick on the target line
      const targetTickId = `dnl-tick-${state.sparseMode.nextId}`;
      const targetTick: DNLCustomTick = {
        id: targetTickId,
        value,
        label: tickLabel,
        line,
        showTick: true,
        showLabel: true, // Always show label for corresponding points (numeric value will be calculated)
        style,
        colour,
        createdAt: Date.now(),
      };

      state.sparseMode.customTicks.push(targetTick);
      state.sparseMode.nextId += 1;

      // Add corresponding tick on other line (same domain position)
      const otherLine = line === 'primary' ? 'secondary' : 'primary';
      const correspondingTickId = `dnl-tick-${state.sparseMode.nextId}`;
      const correspondingTick: DNLCustomTick = {
        id: correspondingTickId,
        value: correspondingValue,
        label: tickLabel, // Same label (or undefined to show numeric value)
        line: otherLine,
        showTick: true,
        showLabel: true, // Always show label for corresponding points (numeric value will be calculated)
        style,
        colour,
        createdAt: Date.now(),
        linkedTickId: targetTickId,
      };

      // Link the target tick to the corresponding tick
      targetTick.linkedTickId = correspondingTickId;

      state.sparseMode.customTicks.push(correspondingTick);
      state.sparseMode.nextId += 1;
      saveToStorage(state);
    },

    /**
     * Remove a custom tick by ID
     * @param removeLinked - If true, also removes the linked tick. Default: true for backward compatibility
     */
    removeCustomTick: (state, action: PayloadAction<{ id: string; removeLinked?: boolean } | string>) => {
      // Support both old format (string) and new format (object)
      const payload = typeof action.payload === 'string' 
        ? { id: action.payload, removeLinked: true }
        : action.payload;
      
      const { id, removeLinked = true } = payload;
      const tickToRemove = state.sparseMode.customTicks.find(t => t.id === id);
      
      if (!tickToRemove) return;
      
      // Find the linked tick BEFORE filtering (if it exists)
      const linkedTickId = tickToRemove.linkedTickId;
      const linkedTick = linkedTickId 
        ? state.sparseMode.customTicks.find(t => t.id === linkedTickId)
        : null;
      
      // Also remove linked tick if requested
      if (removeLinked && linkedTickId) {
        if (linkedTick) {
          // Linked tick exists - clean up its link before removing
          linkedTick.linkedTickId = undefined;
          // Remove both the tick and its linked tick
          state.sparseMode.customTicks = state.sparseMode.customTicks.filter(
            t => t.id !== id && t.id !== linkedTickId
          );
        } else {
          // Linked tick doesn't exist (was already deleted) - just remove this tick
          // and clean up any references to it
          state.sparseMode.customTicks = state.sparseMode.customTicks.filter(
            t => t.id !== id
          );
          // Clean up any ticks that reference this deleted tick
          state.sparseMode.customTicks.forEach(t => {
            if (t.linkedTickId === id) {
              t.linkedTickId = undefined;
            }
          });
        }
      } else {
        // Remove only this tick
        state.sparseMode.customTicks = state.sparseMode.customTicks.filter(
          t => t.id !== id
        );
        // Remove the link from the linked tick if it exists and still exists in the array
        if (linkedTickId && linkedTick) {
          linkedTick.linkedTickId = undefined;
        }
        // Also check if any other tick has this tick as its linked tick and remove that link
        // This handles the case where a point was deleted but its linked point still references it
        state.sparseMode.customTicks.forEach(t => {
          if (t.linkedTickId === id) {
            t.linkedTickId = undefined;
          }
        });
      }
      
      saveToStorage(state);
    },

    /**
     * Remove a custom tick by value and line
     */
    removeCustomTickByValue: (state, action: PayloadAction<{
      value: number;
      line: 'primary' | 'secondary' | 'both';
    }>) => {
      const { value, line } = action.payload;
      state.sparseMode.customTicks = state.sparseMode.customTicks.filter(
        tick => !(Math.abs(tick.value - value) < 1e-10 && 
                 (tick.line === line || tick.line === 'both' || line === 'both'))
      );
      saveToStorage(state);
    },

    /**
     * Update a custom tick
     */
    updateCustomTick: (state, action: PayloadAction<{
      id: string;
      updates: Partial<Omit<DNLCustomTick, 'id' | 'createdAt'>>;
    }>) => {
      const { id, updates } = action.payload;
      const tick = state.sparseMode.customTicks.find(t => t.id === id);
      if (tick) {
        Object.assign(tick, updates);
        saveToStorage(state);
      }
    },

    /**
     * Toggle tick visibility
     */
    toggleCustomTickVisible: (state, action: PayloadAction<string>) => {
      const tick = state.sparseMode.customTicks.find(t => t.id === action.payload);
      if (tick) {
        tick.showTick = !tick.showTick;
        saveToStorage(state);
      }
    },

    /**
     * Toggle label visibility for a custom tick
     */
    toggleCustomTickLabelVisible: (state, action: PayloadAction<string>) => {
      const tick = state.sparseMode.customTicks.find(t => t.id === action.payload);
      if (tick) {
        tick.showLabel = !tick.showLabel;
        saveToStorage(state);
      }
    },

    /**
     * Set custom tick style (for "?" unknown values)
     */
    setCustomTickStyle: (state, action: PayloadAction<{
      id: string;
      style: 'normal' | 'highlighted' | 'question';
    }>) => {
      const { id, style } = action.payload;
      const tick = state.sparseMode.customTicks.find(t => t.id === id);
      if (tick) {
        tick.style = style;
        // If setting to question style, also set the label to "?"
        if (style === 'question' && !tick.label) {
          tick.label = '?';
        }
        saveToStorage(state);
      }
    },

    /**
     * Clear all custom ticks
     */
    clearAllCustomTicks: (state) => {
      state.sparseMode.customTicks = [];
      state.sparseMode.nextId = 1;
      saveToStorage(state);
    },

    /**
     * Clear custom ticks for a specific line only
     */
    clearCustomTicksForLine: (state, action: PayloadAction<'primary' | 'secondary'>) => {
      state.sparseMode.customTicks = state.sparseMode.customTicks.filter(
        tick => tick.line !== action.payload && tick.line !== 'both'
      );
      saveToStorage(state);
    },

    // =========================================================================
    // LINE LABEL ACTIONS
    // =========================================================================

    /**
     * Set primary line label
     */
    setPrimaryLabel: (state, action: PayloadAction<Partial<LineLabel>>) => {
      state.primaryLabel = { ...state.primaryLabel, ...action.payload };
      saveToStorage(state);
    },

    /**
     * Set secondary line label
     */
    setSecondaryLabel: (state, action: PayloadAction<Partial<LineLabel>>) => {
      state.secondaryLabel = { ...state.secondaryLabel, ...action.payload };
      saveToStorage(state);
    },

    /**
     * Toggle primary label visibility
     */
    togglePrimaryLabelVisible: (state) => {
      state.primaryLabel.visible = !state.primaryLabel.visible;
      saveToStorage(state);
    },

    /**
     * Toggle secondary label visibility
     */
    toggleSecondaryLabelVisible: (state) => {
      state.secondaryLabel.visible = !state.secondaryLabel.visible;
      saveToStorage(state);
    },

    // =========================================================================
    // UNIT SUFFIX ACTIONS
    // =========================================================================

    /**
     * Set primary line unit suffix
     */
    setPrimaryUnit: (state, action: PayloadAction<Partial<UnitSuffix>>) => {
      state.primaryUnit = { ...state.primaryUnit, ...action.payload };
      saveToStorage(state);
    },

    /**
     * Set secondary line unit suffix
     */
    setSecondaryUnit: (state, action: PayloadAction<Partial<UnitSuffix>>) => {
      state.secondaryUnit = { ...state.secondaryUnit, ...action.payload };
      saveToStorage(state);
    },

    /**
     * Toggle primary unit enabled
     */
    togglePrimaryUnitEnabled: (state) => {
      state.primaryUnit.enabled = !state.primaryUnit.enabled;
      saveToStorage(state);
    },

    /**
     * Toggle secondary unit enabled
     */
    toggleSecondaryUnitEnabled: (state) => {
      state.secondaryUnit.enabled = !state.secondaryUnit.enabled;
      saveToStorage(state);
    },

    // =========================================================================
    // MAPPING ARROWS ACTIONS
    // =========================================================================

    /**
     * Toggle mapping arrows feature on/off
     */
    toggleMappingArrows: (state) => {
      state.mappingArrows.enabled = !state.mappingArrows.enabled;
      saveToStorage(state);
    },

    /**
     * Set default arrow style
     */
    setArrowStyle: (state, action: PayloadAction<'solid' | 'dashed' | 'dotted'>) => {
      state.mappingArrows.defaultStyle = action.payload;
      // Update all existing arrows to use the new default style
      state.mappingArrows.arrows.forEach(arrow => {
        arrow.style = action.payload;
      });
      saveToStorage(state);
    },

    /**
     * Toggle show multiplier labels by default
     */
    toggleShowMultiplierLabels: (state) => {
      state.mappingArrows.showMultiplierLabels = !state.mappingArrows.showMultiplierLabels;
      // Update all existing arrows to use the new default
      state.mappingArrows.arrows.forEach(arrow => {
        arrow.showMultiplierLabel = state.mappingArrows.showMultiplierLabels;
      });
      saveToStorage(state);
    },

    /**
     * Set default arrow color
     */
    setArrowColour: (state, action: PayloadAction<string>) => {
      state.mappingArrows.defaultColour = action.payload;
      // Update all existing arrows to use the new default color
      state.mappingArrows.arrows.forEach(arrow => {
        arrow.colour = action.payload;
      });
      saveToStorage(state);
    },

    /**
     * Auto-generate arrows from custom ticks that exist on both lines
     */
    generateArrowsFromCustomTicks: (state) => {
      const arrows: MappingArrow[] = [];
      const ticks = state.sparseMode.customTicks;
      
      // Find ticks that exist on both lines or have linked pairs
      ticks.forEach(tick => {
        if (tick.line === 'both' || tick.linkedTickId) {
          // Check if arrow already exists for this value
          const existingArrow = arrows.find(a => Math.abs(a.value - tick.value) < 1e-10);
          if (!existingArrow) {
            arrows.push({
              id: `arrow-${tick.id}`,
              value: tick.value,
              visible: true,
              style: state.mappingArrows.defaultStyle,
              showArrowHeads: 'both',
              showMultiplierLabel: state.mappingArrows.showMultiplierLabels,
              colour: state.mappingArrows.defaultColour,
            });
          }
        }
      });
      
      state.mappingArrows.arrows = arrows;
      saveToStorage(state);
    },

    /**
     * Add a mapping arrow
     */
    addMappingArrow: (state, action: PayloadAction<{
      value: number;
      style?: 'solid' | 'dashed' | 'dotted';
      showMultiplierLabel?: boolean;
      colour?: string;
    }>) => {
      const { value, style, showMultiplierLabel, colour } = action.payload;
      
      // Check if arrow already exists for this value
      const existingArrow = state.mappingArrows.arrows.find(
        a => Math.abs(a.value - value) < 1e-10
      );
      
      if (!existingArrow) {
        const newArrow: MappingArrow = {
          id: `arrow-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          value,
          visible: true,
          style: style || state.mappingArrows.defaultStyle,
          showArrowHeads: 'both',
          showMultiplierLabel: showMultiplierLabel ?? state.mappingArrows.showMultiplierLabels,
          colour: colour || state.mappingArrows.defaultColour,
        };
        state.mappingArrows.arrows.push(newArrow);
        saveToStorage(state);
      }
    },

    /**
     * Remove a mapping arrow by ID
     */
    removeMappingArrow: (state, action: PayloadAction<string>) => {
      state.mappingArrows.arrows = state.mappingArrows.arrows.filter(
        arrow => arrow.id !== action.payload
      );
      saveToStorage(state);
    },

    /**
     * Update a mapping arrow
     */
    updateMappingArrow: (state, action: PayloadAction<{
      id: string;
      updates: Partial<Omit<MappingArrow, 'id'>>;
    }>) => {
      const { id, updates } = action.payload;
      const arrow = state.mappingArrows.arrows.find(a => a.id === id);
      if (arrow) {
        Object.assign(arrow, updates);
        saveToStorage(state);
      }
    },

    /**
     * Clear all mapping arrows
     */
    clearAllMappingArrows: (state) => {
      state.mappingArrows.arrows = [];
      saveToStorage(state);
    },

    // =========================================================================
    // BULK ACTIONS
    // =========================================================================

    /**
     * Reset all DNL tools to defaults
     */
    resetDNLTools: (state) => {
      Object.assign(state, defaultDNLToolsStateLocal);
      saveToStorage(state);
    },

    /**
     * Load DNL tools state (e.g., from shared scene)
     */
    loadDNLToolsState: (state, action: PayloadAction<DNLToolsState>) => {
      Object.assign(state, action.payload);
      saveToStorage(state);
    },

    // =========================================================================
    // PHASE 5: ALTERNATIVE VIEWS ACTIONS
    // =========================================================================

    /**
     * Capture current state as View A
     * Stores current custom ticks and labels as the View A configuration
     */
    captureAsViewA: (state) => {
      const viewConfig = captureCurrentView(
        state.sparseMode.customTicks,
        state.primaryLabel,
        state.secondaryLabel
      );
      state.alternativeViews.viewA = viewConfig;
      state.alternativeViews.isConfigured = true;
      saveToStorage(state);
    },

    /**
     * Capture current state as View B
     * Stores current custom ticks and labels as the View B configuration
     */
    captureAsViewB: (state) => {
      const viewConfig = captureCurrentView(
        state.sparseMode.customTicks,
        state.primaryLabel,
        state.secondaryLabel
      );
      state.alternativeViews.viewB = viewConfig;
      state.alternativeViews.isConfigured = true;
      saveToStorage(state);
    },

    /**
     * Switch to View A
     * Applies the stored View A configuration to current state
     */
    switchToViewA: (state) => {
      if (!state.alternativeViews.isConfigured) {
        console.warn('Views not configured yet');
        return;
      }

      const { customTicks, primaryLabel, secondaryLabel } = 
        applyViewConfiguration(state.alternativeViews.viewA);

      // Clear existing custom ticks
      state.sparseMode.customTicks = [];
      state.sparseMode.nextId = 1;

      // Apply new ticks from View A
      customTicks.forEach(tickData => {
        const newTick: DNLCustomTick = {
          id: `tick-${state.sparseMode.nextId++}`,
          value: tickData.value,
          label: tickData.label,
          line: tickData.line,
          showTick: tickData.showTick,
          showLabel: tickData.showLabel,
          style: tickData.style,
          colour: tickData.colour,
          createdAt: Date.now(),
        };
        state.sparseMode.customTicks.push(newTick);
      });

      // Apply labels
      state.primaryLabel.text = primaryLabel.text;
      state.primaryLabel.subtext = primaryLabel.subtext;
      state.secondaryLabel.text = secondaryLabel.text;
      state.secondaryLabel.subtext = secondaryLabel.subtext;

      // Update current view
      state.alternativeViews.currentView = 'A';
      saveToStorage(state);
    },

    /**
     * Switch to View B
     * Applies the stored View B configuration to current state
     */
    switchToViewB: (state) => {
      if (!state.alternativeViews.isConfigured) {
        console.warn('Views not configured yet');
        return;
      }

      const { customTicks, primaryLabel, secondaryLabel } = 
        applyViewConfiguration(state.alternativeViews.viewB);

      // Clear existing custom ticks
      state.sparseMode.customTicks = [];
      state.sparseMode.nextId = 1;

      // Apply new ticks from View B
      customTicks.forEach(tickData => {
        const newTick: DNLCustomTick = {
          id: `tick-${state.sparseMode.nextId++}`,
          value: tickData.value,
          label: tickData.label,
          line: tickData.line,
          showTick: tickData.showTick,
          showLabel: tickData.showLabel,
          style: tickData.style,
          colour: tickData.colour,
          createdAt: Date.now(),
        };
        state.sparseMode.customTicks.push(newTick);
      });

      // Apply labels
      state.primaryLabel.text = primaryLabel.text;
      state.primaryLabel.subtext = primaryLabel.subtext;
      state.secondaryLabel.text = secondaryLabel.text;
      state.secondaryLabel.subtext = secondaryLabel.subtext;

      // Update current view
      state.alternativeViews.currentView = 'B';
      saveToStorage(state);
    },

    /**
     * Toggle between View A and View B
     */
    toggleView: (state) => {
      if (!state.alternativeViews.isConfigured) {
        console.warn('Views not configured yet');
        return;
      }

      if (state.alternativeViews.currentView === 'A') {
        // Switch to B (reuse switchToViewB logic)
        const { customTicks, primaryLabel, secondaryLabel } = 
          applyViewConfiguration(state.alternativeViews.viewB);

        state.sparseMode.customTicks = [];
        state.sparseMode.nextId = 1;

        customTicks.forEach(tickData => {
          const newTick: DNLCustomTick = {
            id: `tick-${state.sparseMode.nextId++}`,
            value: tickData.value,
            label: tickData.label,
            line: tickData.line,
            showTick: tickData.showTick,
            showLabel: tickData.showLabel,
            style: tickData.style,
            colour: tickData.colour,
            createdAt: Date.now(),
          };
          state.sparseMode.customTicks.push(newTick);
        });

        state.primaryLabel.text = primaryLabel.text;
        state.primaryLabel.subtext = primaryLabel.subtext;
        state.secondaryLabel.text = secondaryLabel.text;
        state.secondaryLabel.subtext = secondaryLabel.subtext;
        state.alternativeViews.currentView = 'B';
      } else {
        // Switch to A (reuse switchToViewA logic)
        const { customTicks, primaryLabel, secondaryLabel } = 
          applyViewConfiguration(state.alternativeViews.viewA);

        state.sparseMode.customTicks = [];
        state.sparseMode.nextId = 1;

        customTicks.forEach(tickData => {
          const newTick: DNLCustomTick = {
            id: `tick-${state.sparseMode.nextId++}`,
            value: tickData.value,
            label: tickData.label,
            line: tickData.line,
            showTick: tickData.showTick,
            showLabel: tickData.showLabel,
            style: tickData.style,
            colour: tickData.colour,
            createdAt: Date.now(),
          };
          state.sparseMode.customTicks.push(newTick);
        });

        state.primaryLabel.text = primaryLabel.text;
        state.primaryLabel.subtext = primaryLabel.subtext;
        state.secondaryLabel.text = secondaryLabel.text;
        state.secondaryLabel.subtext = secondaryLabel.subtext;
        state.alternativeViews.currentView = 'A';
      }

      saveToStorage(state);
    },

    /**
     * Set animations enabled state
     */
    setAnimationsEnabled: (state, action: PayloadAction<boolean>) => {
      state.alternativeViews.animationsEnabled = action.payload;
      saveToStorage(state);
    },

    /**
     * Toggle animations enabled
     */
    toggleAnimations: (state) => {
      state.alternativeViews.animationsEnabled = !state.alternativeViews.animationsEnabled;
      saveToStorage(state);
    },

    /**
     * Set tooltip visibility
     */
    setTooltipVisible: (state, action: PayloadAction<boolean>) => {
      state.alternativeViews.showTooltip = action.payload;
      saveToStorage(state);
    },

    /**
     * Dismiss the pedagogical tooltip
     */
    dismissTooltip: (state) => {
      state.alternativeViews.showTooltip = false;
      saveToStorage(state);
    },

    /**
     * Reset alternative views to default state
     */
    resetAlternativeViews: (state) => {
      state.alternativeViews = { ...defaultAlternativeViews };
      saveToStorage(state);
    },

    // =========================================================================
    // PHASE 6: CONTEXT PRESETS
    // =========================================================================

    /**
     * Apply a context preset
     * Sets labels, and optionally scale factor
     * Note: Number formats and scale factor are applied in the component
     * via separate actions to the numberLinesSlice and secondaryLineSlice
     */
    applyContextPreset: (
      state,
      action: PayloadAction<{
        presetId: DNLContextPreset;
        applyScaleFactor?: boolean;
      }>
    ) => {
      const { presetId, applyScaleFactor = true } = action.payload;
      const config = CONTEXT_PRESETS[presetId];
      
      if (!config) {
        console.warn(`Unknown preset: ${presetId}`);
        return;
      }
      
      // Apply labels
      state.primaryLabel = {
        text: config.primaryLabel,
        subtext: config.primarySubtext,
        position: 'left',
        visible: true,
      };
      
      state.secondaryLabel = {
        text: config.secondaryLabel,
        subtext: config.secondarySubtext,
        position: 'left',
        visible: true,
      };
      
      // Mark preset as applied
      state.contextPreset.selectedPreset = presetId;
      state.contextPreset.isPresetApplied = true;
      
      // Note: Number formats and scale factor are applied in the component
      // via separate actions to the numberLinesSlice and secondaryLineSlice
      
      saveToStorage(state);
    },

    /**
     * Mark configuration as custom (user has modified preset)
     */
    markAsCustom: (state) => {
      state.contextPreset.selectedPreset = 'custom';
      state.contextPreset.isPresetApplied = false;
      saveToStorage(state);
    },

    /**
     * Select a preset without applying it
     */
    selectPreset: (state, action: PayloadAction<DNLContextPreset>) => {
      state.contextPreset.selectedPreset = action.payload;
      saveToStorage(state);
    },

    /**
     * Toggle showing preset descriptions
     */
    togglePresetDescriptions: (state) => {
      state.contextPreset.showDescriptions = !state.contextPreset.showDescriptions;
      saveToStorage(state);
    },

    // =========================================================================
    // PHASE 7: RATIO TABLE OVERLAY
    // =========================================================================

    /**
     * Toggle ratio table visibility
     */
    toggleRatioTable: (state) => {
      state.ratioTable.visible = !state.ratioTable.visible;
      saveToStorage(state);
    },

    /**
     * Set ratio table visibility
     */
    setRatioTableVisible: (state, action: PayloadAction<boolean>) => {
      state.ratioTable.visible = action.payload;
      saveToStorage(state);
    },

    /**
     * Set table position mode
     */
    setTablePositionMode: (state, action: PayloadAction<TablePosition>) => {
      state.ratioTable.positionMode = action.payload;
      saveToStorage(state);
    },

    /**
     * Set custom table position
     */
    setTableCustomPosition: (state, action: PayloadAction<{ x: number; y: number }>) => {
      state.ratioTable.customPosition = action.payload;
      state.ratioTable.positionMode = 'custom';
      saveToStorage(state);
    },

    /**
     * Toggle auto-sync with custom ticks
     */
    toggleTableAutoSync: (state) => {
      state.ratioTable.autoSync = !state.ratioTable.autoSync;
      if (state.ratioTable.autoSync) {
        // Re-sync immediately
        const updatedTable = syncTableWithCustomTicksHelper(state);
        state.ratioTable.cells = updatedTable.cells;
        state.ratioTable.columnCount = updatedTable.columnCount;
      }
      saveToStorage(state);
    },

    /**
     * Sync table with custom ticks
     * Creates columns for each custom tick value pair
     */
    syncTableWithCustomTicks: (state) => {
      const updatedTable = syncTableWithCustomTicksHelper(state);
      state.ratioTable.cells = updatedTable.cells;
      state.ratioTable.columnCount = updatedTable.columnCount;
      saveToStorage(state);
    },

    /**
     * Update a table cell value
     * Syncs with custom tick if linked
     */
    updateTableCell: (
      state,
      action: PayloadAction<{ cellId: string; value: number | null }>
    ) => {
      const { cellId, value } = action.payload;
      const cell = state.ratioTable.cells.find(c => c.id === cellId);
      
      if (!cell) return;
      
      cell.value = value;
      
      // If linked to a custom tick, update the tick
      if (cell.linkedTickId && value !== null) {
        const tick = state.sparseMode.customTicks.find(t => t.id === cell.linkedTickId);
        if (tick) {
          tick.value = value;
        }
      }
      
      saveToStorage(state);
    },

    /**
     * Toggle cell visibility
     */
    toggleCellVisibility: (state, action: PayloadAction<string>) => {
      const cell = state.ratioTable.cells.find(c => c.id === action.payload);
      if (cell) {
        cell.visible = !cell.visible;
        saveToStorage(state);
      }
    },

    /**
     * Add a new column to the table
     */
    addTableColumn: (state) => {
      const newColumnIndex = state.ratioTable.columnCount;
      
      // Add cells for new column
      state.ratioTable.cells.push(
        {
          id: `cell-primary-${newColumnIndex}`,
          row: 0,
          column: newColumnIndex,
          value: null,
          visible: true,
          editable: true,
        },
        {
          id: `cell-secondary-${newColumnIndex}`,
          row: 1,
          column: newColumnIndex,
          value: null,
          visible: true,
          editable: true,
        }
      );
      
      state.ratioTable.columnCount++;
      saveToStorage(state);
    },

    /**
     * Remove a column from the table
     */
    removeTableColumn: (state, action: PayloadAction<number>) => {
      const columnIndex = action.payload;
      
      // Remove cells in this column
      state.ratioTable.cells = state.ratioTable.cells.filter(
        cell => cell.column !== columnIndex
      );
      
      // Reindex remaining columns
      state.ratioTable.cells.forEach(cell => {
        if (cell.column > columnIndex) {
          cell.column--;
        }
      });
      
      state.ratioTable.columnCount--;
      saveToStorage(state);
    },

    /**
     * Clear all table data
     */
    clearRatioTable: (state) => {
      state.ratioTable.cells = [];
      state.ratioTable.columnCount = 0;
      saveToStorage(state);
    },

    /**
     * Set table dragging state
     */
    setTableDragging: (state, action: PayloadAction<boolean>) => {
      state.ratioTable.isDragging = action.payload;
    },
  },
});

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Helper: Sync table with custom ticks
 */
function syncTableWithCustomTicksHelper(state: DNLToolsState): { 
  cells: RatioTableCell[]; 
  columnCount: number 
} {
  const { customTicks } = state.sparseMode;
  
  // Get unique values from custom ticks
  const uniqueValues = new Set<number>();
  customTicks.forEach(tick => uniqueValues.add(tick.value));
  
  const sortedValues = Array.from(uniqueValues).sort((a, b) => a - b);
  
  // Create cells for each value
  const cells: RatioTableCell[] = [];
  sortedValues.forEach((value, colIndex) => {
    // Find ticks for this value
    const primaryTick = customTicks.find(t => t.value === value && (t.line === 'primary' || t.line === 'both'));
    const secondaryTick = customTicks.find(t => t.value === value && (t.line === 'secondary' || t.line === 'both'));
    
    // Primary row cell
    cells.push({
      id: `cell-primary-${colIndex}`,
      row: 0,
      column: colIndex,
      value: primaryTick ? value : null,
      visible: true,
      editable: true,
      linkedTickId: primaryTick?.id,
    });
    
    // Secondary row cell (scaled value)
    cells.push({
      id: `cell-secondary-${colIndex}`,
      row: 1,
      column: colIndex,
      value: secondaryTick ? value : null,
      visible: true,
      editable: true,
      linkedTickId: secondaryTick?.id,
    });
  });
  
  return { cells, columnCount: sortedValues.length };
}

// =============================================================================
// EXPORTS
// =============================================================================

export const {
  // Sparse mode
  toggleSparseMode,
  setSparseModeEnabled,
  toggleHideRegularTicks,
  setHideRegularTicks,
  toggleShowZero,
  // Custom ticks
  addCustomTick,
  addCustomTickWithCorresponding,
  removeCustomTick,
  removeCustomTickByValue,
  updateCustomTick,
  toggleCustomTickVisible,
  toggleCustomTickLabelVisible,
  setCustomTickStyle,
  clearAllCustomTicks,
  clearCustomTicksForLine,
  // Line labels
  setPrimaryLabel,
  setSecondaryLabel,
  togglePrimaryLabelVisible,
  toggleSecondaryLabelVisible,
  // Unit suffixes
  setPrimaryUnit,
  setSecondaryUnit,
  togglePrimaryUnitEnabled,
  toggleSecondaryUnitEnabled,
  // Mapping arrows
  toggleMappingArrows,
  setArrowStyle,
  toggleShowMultiplierLabels,
  setArrowColour,
  generateArrowsFromCustomTicks,
  addMappingArrow,
  removeMappingArrow,
  updateMappingArrow,
  clearAllMappingArrows,
  // Bulk actions
  resetDNLTools,
  loadDNLToolsState,
  // Phase 5: Alternative Views
  captureAsViewA,
  captureAsViewB,
  switchToViewA,
  switchToViewB,
  toggleView,
  setAnimationsEnabled,
  toggleAnimations,
  setTooltipVisible,
  dismissTooltip,
  resetAlternativeViews,
  // Phase 6: Context Presets
  applyContextPreset,
  markAsCustom,
  selectPreset,
  togglePresetDescriptions,
  // Phase 7: Ratio Table
  toggleRatioTable,
  setRatioTableVisible,
  setTablePositionMode,
  setTableCustomPosition,
  toggleTableAutoSync,
  syncTableWithCustomTicks,
  updateTableCell,
  toggleCellVisibility,
  addTableColumn,
  removeTableColumn,
  clearRatioTable,
  setTableDragging,
} = dnlToolsSlice.actions;

export default dnlToolsSlice.reducer;

// =============================================================================
// SELECTORS
// =============================================================================

/**
 * Select all custom ticks
 */
export const selectAllCustomTicks = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.sparseMode.customTicks;

/**
 * Select custom ticks for a specific line
 */
export const selectCustomTicksForLine = (
  state: { dnlTools: DNLToolsState },
  line: 'primary' | 'secondary'
) =>
  state.dnlTools.sparseMode.customTicks.filter(
    tick => tick.line === line || tick.line === 'both'
  );

/**
 * Select custom ticks within a domain
 */
export const selectCustomTicksInDomain = (
  state: { dnlTools: DNLToolsState },
  domain: { start: number; end: number },
  line?: 'primary' | 'secondary'
) =>
  state.dnlTools.sparseMode.customTicks.filter(tick => {
    const inDomain = tick.value >= domain.start && tick.value <= domain.end;
    const matchesLine = !line || tick.line === line || tick.line === 'both';
    return inDomain && matchesLine;
  });

/**
 * Select sparse mode state
 */
export const selectSparseMode = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.sparseMode;

/**
 * Check if sparse mode is enabled
 */
export const selectIsSparseModeEnabled = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.sparseMode.enabled;

/**
 * Select line labels
 */
export const selectLineLabels = (state: { dnlTools: DNLToolsState }) => ({
  primary: state.dnlTools.primaryLabel,
  secondary: state.dnlTools.secondaryLabel,
});

/**
 * Select unit suffixes
 */
export const selectUnitSuffixes = (state: { dnlTools: DNLToolsState }) => ({
  primary: state.dnlTools.primaryUnit,
  secondary: state.dnlTools.secondaryUnit,
});

/**
 * Select alternative views state
 */
export const selectAlternativeViews = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.alternativeViews;

/**
 * Check if views are configured
 */
export const selectIsViewsConfigured = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.alternativeViews.isConfigured;

/**
 * Get current view mode
 */
export const selectCurrentView = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.alternativeViews.currentView;

/**
 * Check if animations are enabled
 */
export const selectAnimationsEnabled = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.alternativeViews.animationsEnabled;

/**
 * Check if tooltip should be shown
 */
export const selectShowTooltip = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.alternativeViews.showTooltip;

/**
 * Select current preset
 */
export const selectCurrentPreset = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.contextPreset.selectedPreset;

/**
 * Check if preset is applied
 */
export const selectIsPresetApplied = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.contextPreset.isPresetApplied;

/**
 * Check if showing descriptions
 */
export const selectShowPresetDescriptions = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.contextPreset.showDescriptions;

/**
 * Select ratio table state
 */
export const selectRatioTable = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.ratioTable;

/**
 * Check if ratio table is visible
 */
export const selectIsRatioTableVisible = (state: { dnlTools: DNLToolsState }) =>
  state.dnlTools.ratioTable.visible;

/**
 * Get table cells for a specific column
 */
export const selectTableColumn = (
  state: { dnlTools: DNLToolsState },
  columnIndex: number
) =>
  state.dnlTools.ratioTable.cells.filter(cell => cell.column === columnIndex);
